/**
 *
 * Sales Description
 *
 */
import React, { Fragment, useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { Box } from '@eriksdigital/atomic-ui/components';
import { Text } from 'app/components/atoms';
import { configuratorSteps } from 'utils/configuratorSteps';
import { SelectAdapter } from 'app/components/molecules';
import { Languages, translations } from './constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectLanguage } from 'app/features/erpSystem/slice/selectors';
import {
  selectBracket,
  selectCoupling,
} from 'app/features/couplings/slice/selectors';

interface Props {
  safetyFactor: number | undefined;
  supplyPressure: string | undefined;
  diffPressure: string;
  mediumType: string;
  savedItems: any;
  savedStemExtensions: any;
  savedPucks: any;
  savedSpeedControlBlock: any;
  actuatorBasicForm: any;
}

export const SalesDescription = ({
  safetyFactor,
  supplyPressure,
  diffPressure,
  mediumType,
  savedItems,
  savedStemExtensions,
  savedPucks,
  savedSpeedControlBlock,
  actuatorBasicForm,
}: Props) => {
  const intl = useIntl();
  const userLanguageCode = useSelector(selectLanguage);
  const [lang, setLang] = useState<any>(Languages[0]);
  const coupling = useSelector(selectCoupling);
  const bracket = useSelector(selectBracket);

  const {
    colors: {
      default: { blue2, black },
    },
    fonts: {
      default: {
        fontSize: { ft24, ft14 },
        fontWeight: { normal },
      },
    },
  } = useTheme();

  useEffect(() => {
    if (userLanguageCode) {
      const lang = Languages.find(
        (lang: any) => lang.value === userLanguageCode,
      );
      setLang(lang);
    }
  }, [userLanguageCode]);

  const handleSelect = (selectedOption: any) => {
    setLang(selectedOption);
  };

  const stepNames = (stepName: string) => {
    return translations[stepName][lang.value];
  };

  const mountingType = savedItems[5].item.services.toLowerCase();

  return (
    <>
      <Box data-testid="sales-description-title" marginBottom="sp16">
        <Text
          fontSize={ft24}
          fontWeight={normal}
          color={blue2}
          className="bold-style"
        >
          <FormattedMessage
            id="overview.sales-description.title"
            defaultMessage="Sales description"
            description="Title text for sales description block"
          />
        </Text>
      </Box>
      <Box data-testid="sales-description-content">
        <SelectAdapter
          dataTestId="language-selector"
          id="language-selector"
          name="LanguageSelector"
          options={Languages}
          handleSelect={handleSelect}
          selected={lang}
        />
        <div
          id="sales-text-description"
          contentEditable="false"
          style={{
            padding: '16px',
            border: '1px solid #c1c7cd',
            lineHeight: '1.3',
          }}
        >
          {Object.keys(savedItems).map(index => {
            return (
              <Fragment key={index}>
                {savedItems[index].item !== null && (
                  <Box
                    marginBottom="sp16"
                    data-testid={configuratorSteps[parseInt(index) - 1].name}
                  >
                    {index !== '2' ? (
                      <Text
                        fontSize={ft14}
                        color={black}
                        className="bold-style"
                      >
                        {stepNames(configuratorSteps[parseInt(index) - 1].name)}
                        :
                      </Text>
                    ) : (
                      <Text
                        fontSize={ft14}
                        color={black}
                        className="bold-style"
                      >
                        {stepNames('operations')}:
                      </Text>
                    )}
                    {index === '2' && actuatorBasicForm !== 'gearbox' && (
                      <>
                        <Box marginTop="sp8">
                          <Text fontSize={ft14} color={black}>
                            {translations.actuatorHeading[lang.value]}
                          </Text>
                        </Box>
                        <Box marginTop="sp8">
                          <Box>
                            <Text color={black} fontSize={ft14}>
                              {translations.diffPress[lang.value]}:{' '}
                              {diffPressure}
                            </Text>
                          </Box>
                          {supplyPressure && (
                            <Box>
                              <Text color={black} fontSize={ft14}>
                                {translations.supplyPress[lang.value]}:{' '}
                                {supplyPressure}
                              </Text>
                            </Box>
                          )}
                          <Box>
                            <Text color={black} fontSize={ft14}>
                              {translations.safety[lang.value]}: {safetyFactor}{' '}
                              {mediumType && '- '}
                              {mediumType &&
                                intl.formatMessage({
                                  id: `filters.${configuratorSteps[1].name}.label.${mediumType}`,
                                  defaultMessage: mediumType,
                                  description: `Label for ${mediumType} current filter`,
                                })}{' '}
                              {mediumType && translations.medium[lang.value]}
                            </Text>
                          </Box>
                        </Box>
                      </>
                    )}
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        {index === '5'
                          ? translations.mounting[lang.value] +
                            ': ' +
                            translations[mountingType][lang.value]
                          : savedItems[index].item.brand +
                            ' ' +
                            savedItems[index].item.descriptionMap[lang.value]}
                      </Text>
                    </Box>
                    {index !== '5' && (
                      <Box marginTop="sp8">
                        <Text fontSize={ft14} color={black}>
                          ERIKS {translations.articleNumber[lang.value]}:{' '}
                          {savedItems[index].item.erpPartNumber}
                        </Text>
                      </Box>
                    )}
                    {index === '1' &&
                      savedItems[index].item.couplingsPartnumber !== null &&
                      savedItems[index].item.bracketsPartnumber !== null &&
                      coupling !== null &&
                      bracket !== null && (
                        <>
                          <Box marginTop="sp8">
                            <Text
                              fontSize={ft14}
                              color={black}
                              className="bold-style"
                            >
                              {translations.couplings[lang.value]}:{' '}
                            </Text>
                          </Box>
                          <Box marginTop="sp8">
                            <Text fontSize={ft14} color={black}>
                              {coupling?.descriptionMap[lang.value]}
                            </Text>
                          </Box>
                          <Box marginTop="sp8">
                            <Text fontSize={ft14} color={black}>
                              ERIKS {translations.articleNumber[lang.value]}:{' '}
                              {coupling?.erpPartNumber}
                            </Text>
                          </Box>
                          <Box marginTop="sp8">
                            <Text
                              fontSize={ft14}
                              color={black}
                              className="bold-style"
                            >
                              {translations.brackets[lang.value]}:{' '}
                            </Text>
                            <Box marginTop="sp8">
                              <Text fontSize={ft14} color={black}>
                                {bracket?.descriptionMap[lang.value]}
                              </Text>
                            </Box>
                            <Box marginTop="sp8">
                              <Text fontSize={ft14} color={black}>
                                ERIKS {translations.articleNumber[lang.value]}:{' '}
                                {bracket?.erpPartNumber}
                              </Text>
                            </Box>
                          </Box>
                        </>
                      )}
                  </Box>
                )}
                {index === '1' && savedStemExtensions.length !== 0 && (
                  <Box marginBottom="sp16" data-test-id="stem-extension">
                    <Text fontSize={ft14} color={black} className="bold-style">
                      {stepNames('stemExtension')}:
                    </Text>
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        {savedStemExtensions[0].brand}{' '}
                        {savedStemExtensions[0].descriptionMap[lang.value]}
                      </Text>
                    </Box>
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        ERIKS {translations.articleNumber[lang.value]}:{' '}
                        {savedStemExtensions[0].erpPartNumber}
                      </Text>
                    </Box>
                  </Box>
                )}
                {index === '2' &&
                  savedItems[2]?.item.inserts &&
                  savedItems[2]?.item.inserts?.length > 0 && (
                    <Box marginBottom="sp16" data-testid="insert">
                      <Text
                        fontSize={ft14}
                        color={black}
                        className="bold-style"
                      >
                        {translations.insert[lang.value]}:
                      </Text>
                      <Box marginTop="sp8">
                        <Text fontSize={ft14} color={black}>
                          {
                            savedItems[2].item.inserts[0]?.descriptionMap[
                              lang.value
                            ]
                          }
                        </Text>
                      </Box>
                      <Box marginTop="sp8">
                        <Text fontSize={ft14} color={black}>
                          ERIKS {translations.articleNumber[lang.value]}:{' '}
                          {savedItems[2].item.inserts[0]?.erpPartNumber}
                        </Text>
                      </Box>
                    </Box>
                  )}
                {index === '3' && savedPucks !== null && (
                  <Box marginBottom="sp16" data-test-id="pucks">
                    <Text fontSize={ft14} color={black} className="bold-style">
                      {stepNames('pucks')}:
                    </Text>
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        {savedPucks[0].brand}{' '}
                        {savedPucks[0].descriptionMap[lang.value]}
                      </Text>
                    </Box>
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        ERIKS {translations.articleNumber[lang.value]}:{' '}
                        {savedPucks[0].erpPartNumber}
                      </Text>
                    </Box>
                  </Box>
                )}{' '}
                {index === '4' && savedSpeedControlBlock.length !== 0 && (
                  <Box marginBottom="sp16" data-test-id="speed-control-block">
                    <Text fontSize={ft14} color={black} className="bold-style">
                      {stepNames('speedControlBlock')}:
                    </Text>
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        {savedSpeedControlBlock[0].brand}{' '}
                        {savedSpeedControlBlock[0].descriptionMap[lang.value]}
                      </Text>
                    </Box>
                    <Box marginTop="sp8">
                      <Text fontSize={ft14} color={black}>
                        ERIKS {translations.articleNumber[lang.value]}:{' '}
                        {savedSpeedControlBlock[0].erpPartNumber}
                      </Text>
                    </Box>
                  </Box>
                )}
              </Fragment>
            );
          })}
        </div>
      </Box>
    </>
  );
};
